import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog,
  DialogContent
} from '@material-ui/core';
import VectorSearch from './VectorSearch';
import { GetActiveFilters, FilterTags } from '../../signals/filterService';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  closeButton: {
    color: theme.palette.grey[500],
  },
  title: {
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  examplesContainer: {
    marginTop: theme.spacing(6),
    maxWidth: '900px',
    margin: '0 auto',
  },
  examplesTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
  exampleItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#fff',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.03)',
    },
  },
  dialogContent: {
    padding: theme.spacing(2),
    minWidth: '800px',
    minHeight: '600px',
  }
}));

interface ConversationalUIModalProps {
  open: boolean;
  onClose: () => void;
}

const ConversationalUIModal: React.FC<ConversationalUIModalProps> = ({ open, onClose }) => {
  const classes = useStyles();
  const [selectedQuestion] = useState('');

  // State for filters extracted from the application's filter service
  const [facilityNames, setFacilityNames] = useState<string[]>([]);
  const [classificationNames, setClassificationNames] = useState<string[]>([]);
  const [classificationTypes, setClassificationTypes] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Effect to extract filter values from the application's filter service
  useEffect(() => {
    if (open) {
      // Get all active filters from the filter service
      const activeFilters = GetActiveFilters();
      console.log('Active filters:', activeFilters);

      if (activeFilters && activeFilters.length > 0) {
        // Extract facility names - use the display name instead of ID
        const facilities = activeFilters
          .filter(filter => filter.property === FilterTags.report.facility)
          .map(filter => filter.name); // Use name instead of value (ID)
        setFacilityNames(facilities);
        console.log('Extracted facility names:', facilities);

        // Extract classification types (Topic, Hazard, Root Cause)
        const types: string[] = [];

        // Check if Topic filter is active
        if (activeFilters.some(filter => filter.property === FilterTags.report.topic)) {
          types.push('Topic');
        }

        // Check if Hazard filter is active
        if (activeFilters.some(filter => filter.property === FilterTags.report.hazard)) {
          types.push('Hazard');
        }

        // Check if Root Cause filter is active
        if (activeFilters.some(filter => filter.property === FilterTags.report.rootCause)) {
          types.push('RootCause');
        }

        setClassificationTypes(types);
        console.log('Extracted classification types:', types);

        // Extract classification names from Topic, Hazard, and Root Cause filters - use names instead of IDs
        const names = activeFilters
          .filter(filter => (
            filter.property === FilterTags.report.topic ||
            filter.property === FilterTags.report.hazard ||
            filter.property === FilterTags.report.rootCause
          ))
          .map(filter => filter.name); // Use name instead of value (ID)
        setClassificationNames(names);
        console.log('Extracted classification names:', names);

        // Directly get date inputs from the DOM
        console.log('Looking for date inputs in the DOM...');
        const fromDateInput = document.querySelector('input[placeholder="From"]') as HTMLInputElement;
        const toDateInput = document.querySelector('input[placeholder="To"]') as HTMLInputElement;

        let startDateFormatted = '';
        let endDateFormatted = '';

        if (fromDateInput) {
          const dateValue = fromDateInput.value;
          console.log('Found From date input with value:', dateValue);
          if (dateValue && dateValue.includes('/')) {
            const [day, month, year] = dateValue.split('/');
            startDateFormatted = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            console.log('Formatted start date:', startDateFormatted);
          }
        }

        if (toDateInput) {
          const dateValue = toDateInput.value;
          console.log('Found To date input with value:', dateValue);
          if (dateValue && dateValue.includes('/')) {
            const [day, month, year] = dateValue.split('/');
            endDateFormatted = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            console.log('Formatted end date:', endDateFormatted);
          }
        }

        // If we found formatted dates, use them
        if (startDateFormatted) {
          setStartDate(startDateFormatted);
        } else {
          // Fallback to filter service
          const fromDateFilter = activeFilters.find(filter =>
            filter.name === 'From' ||
            filter.property === 'DateFrom' ||
            filter.property === 'ReportDateFrom'
          );

          if (fromDateFilter) {
            const dateValue = fromDateFilter.value;
            console.log('Found From date filter with value:', dateValue);
            if (dateValue && dateValue.includes('/')) {
              const [day, month, year] = dateValue.split('/');
              setStartDate(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
            } else if (dateValue) {
              setStartDate(dateValue);
            }
          }
        }

        if (endDateFormatted) {
          setEndDate(endDateFormatted);
        } else {
          // Fallback to filter service
          const toDateFilter = activeFilters.find(filter =>
            filter.name === 'To' ||
            filter.property === 'DateTo' ||
            filter.property === 'ReportDateTo'
          );

          if (toDateFilter) {
            const dateValue = toDateFilter.value;
            console.log('Found To date filter with value:', dateValue);
            if (dateValue && dateValue.includes('/')) {
              const [day, month, year] = dateValue.split('/');
              setEndDate(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
            } else if (dateValue) {
              setEndDate(dateValue);
            }
          }
        }

        // Hard-coded fallback if we still don't have dates
        if (!startDateFormatted && !endDateFormatted) {
          console.log('Using hard-coded date range as fallback');
          // Get current date for end date
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          const formattedToday = `${year}-${month}-${day}`;

          // Get date 3 months ago for start date
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          const startYear = threeMonthsAgo.getFullYear();
          const startMonth = String(threeMonthsAgo.getMonth() + 1).padStart(2, '0');
          const startDay = String(threeMonthsAgo.getDate()).padStart(2, '0');
          const formattedStart = `${startYear}-${startMonth}-${startDay}`;

          setStartDate(formattedStart);
          setEndDate(formattedToday);
          console.log('Set fallback date range:', formattedStart, 'to', formattedToday);
        }
      }
    }
  }, [open, startDate, endDate]);

  // No example questions - removed as requested

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogContent className={classes.dialogContent}>
        <VectorSearch
          selectedQuestion={selectedQuestion}
          facilityNames={facilityNames}
          classificationNames={classificationNames}
          classificationTypes={classificationTypes}
          startDate={startDate}
          endDate={endDate}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ConversationalUIModal;
